import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  ApiError,
  blockUser,
  deleteCalendarSharePermission,
  listBlockedUsers,
  listShareGrants,
  unblockUser,
  type BlockedUser,
  type CalendarShareGrant,
} from '@/api'
import { useToast } from '@/composables/useToast'
import { reportError } from '@/errorReport'

// The state and the actions of Settings > Sharing & blocked users, shared by all of its pages: the grants on the user's calendars
// and the blocked users are read once when the category opens.
const grants = ref<CalendarShareGrant[]>([])
const blocked = ref<BlockedUser[]>([])
const loaded = ref(false)
const loading = ref(false)
const loadError = ref('')

export function useSharingEditor() {
  const { t } = useI18n()
  const toast = useToast()

  // A failed action: the toast floats at the top of the viewport, so it is seen however far the page is scrolled.
  function fail(e: unknown, key: string): void {
    reportError(e, key)
    toast.error(e, t(key))
  }

  async function load(): Promise<void> {
    loading.value = true
    loadError.value = ''
    try {
      const [g, b] = await Promise.all([listShareGrants(), listBlockedUsers()])
      grants.value = g
      blocked.value = b
      loaded.value = true
    } catch (e) {
      reportError(e, 'settings.err.loadGranted')
      loadError.value = e instanceof ApiError ? e.message : t('settings.err.loadGranted')
    } finally {
      loading.value = false
    }
  }

  async function revokeGrant(grant: CalendarShareGrant): Promise<void> {
    try {
      await deleteCalendarSharePermission(grant.caldav_account_id, grant.caldav_calendar_id, grant.id)
      grants.value = grants.value.filter((g) => g.id !== grant.id)
    } catch (e) {
      fail(e, 'settings.err.revokeGrant')
    }
  }

  // Returns whether the user is blocked now (the form stays filled on a failure).
  async function block(username: string): Promise<boolean> {
    try {
      await blockUser(username.replace(/^@/, ''))
      blocked.value = await listBlockedUsers()
      return true
    } catch (e) {
      fail(e, 'settings.err.block')
      return false
    }
  }

  async function unblock(entry: BlockedUser): Promise<void> {
    try {
      await unblockUser(entry.id)
      blocked.value = blocked.value.filter((b) => b.id !== entry.id)
    } catch (e) {
      fail(e, 'settings.err.unblock')
    }
  }

  return {
    grants,
    blocked,
    loaded,
    loading,
    loadError,
    grantCount: computed(() => grants.value.length),
    blockedCount: computed(() => blocked.value.length),
    load,
    revokeGrant,
    block,
    unblock,
  }
}
